/**
 * DocumentSearch — inline cmdk-backed search for manuscript and guide text.
 *
 * Unlike the command palette, this component is designed to live inside a
 * panel. It accepts pre-indexed document bodies, performs deterministic local
 * scoring, renders snippets, and lets the host decide what "open result"
 * means. The command list itself uses `cmdk`, giving demos keyboard navigation
 * and a familiar searchable-list interaction without re-implementing it.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { Command } from "cmdk";
import { FileText, Search } from "lucide-react";
import React, { useMemo, useState } from "react";
import { GRAY, PRIMARY } from "../../design-system/tokens.js";

export interface DocumentSearchItem {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly subtitle?: string;
  readonly group?: string;
  readonly meta?: string;
  readonly keywords?: readonly string[];
}

export interface DocumentSearchResult<TItem extends DocumentSearchItem = DocumentSearchItem> {
  readonly item: TItem;
  readonly score: number;
  readonly snippet: string;
}

export interface DocumentSearchProps<TItem extends DocumentSearchItem = DocumentSearchItem> {
  readonly items: readonly TItem[];
  readonly onSelect?: (item: TItem) => void;
  readonly title?: string;
  readonly placeholder?: string;
  readonly emptyLabel?: string;
  readonly maxResults?: number;
  readonly initialQuery?: string;
  readonly "aria-label"?: string;
}

interface PreparedDocumentSearchItem<TItem extends DocumentSearchItem> {
  readonly item: TItem;
  readonly title: string;
  readonly subtitle: string;
  readonly group: string;
  readonly meta: string;
  readonly keywordText: string;
  readonly body: string;
  readonly snippetBody: string;
  readonly snippetBodyLower: string;
}

const DEFAULT_MAX_RESULTS = 12;
const SNIPPET_RADIUS = 72;

const SHELL_STYLE: React.CSSProperties = {
  background: "white",
  border: `1px solid ${GRAY[300]}`,
  borderRadius: 8,
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
  color: GRAY[900],
  display: "grid",
  gap: 8,
  padding: 12,
};

const INPUT_WRAP_STYLE: React.CSSProperties = {
  alignItems: "center",
  background: GRAY[50],
  border: `1px solid ${GRAY[300]}`,
  borderRadius: 8,
  display: "flex",
  gap: 8,
  padding: "0 10px",
};

const INPUT_STYLE: React.CSSProperties = {
  background: "transparent",
  border: 0,
  color: GRAY[900],
  fontSize: 13,
  outline: "none",
  padding: "9px 0",
  width: "100%",
};

const LIST_STYLE: React.CSSProperties = {
  display: "grid",
  gap: 4,
  maxHeight: 320,
  overflowY: "auto",
};

const ITEM_STYLE: React.CSSProperties = {
  border: `1px solid ${GRAY[300]}`,
  borderRadius: 8,
  cursor: "pointer",
  display: "grid",
  gap: 4,
  padding: "9px 10px",
};

function normalize(value: string): string {
  return value.toLowerCase().replace(/\s+/g, " ").trim();
}

function words(query: string): string[] {
  return normalize(query)
    .split(" ")
    .map((term) => term.trim())
    .filter((term) => term.length > 0);
}

function prepareSearchItem<TItem extends DocumentSearchItem>(
  item: TItem,
): PreparedDocumentSearchItem<TItem> {
  const snippetBody = compactWhitespace(item.body);
  return {
    item,
    title: normalize(item.title),
    subtitle: normalize(item.subtitle ?? ""),
    group: normalize(item.group ?? ""),
    meta: normalize(item.meta ?? ""),
    keywordText: normalize((item.keywords ?? []).join(" ")),
    body: normalize(item.body),
    snippetBody,
    snippetBodyLower: snippetBody.toLowerCase(),
  };
}

function includesTerm<TItem extends DocumentSearchItem>(
  prepared: PreparedDocumentSearchItem<TItem>,
  term: string,
): boolean {
  return (
    prepared.title.includes(term) ||
    prepared.subtitle.includes(term) ||
    prepared.group.includes(term) ||
    prepared.meta.includes(term) ||
    prepared.keywordText.includes(term) ||
    prepared.body.includes(term)
  );
}

function scorePreparedItem<TItem extends DocumentSearchItem>(
  prepared: PreparedDocumentSearchItem<TItem>,
  terms: readonly string[],
): number {
  if (terms.length === 0) return 1;
  let score = 0;
  for (const term of terms) {
    if (!includesTerm(prepared, term)) return 0;
    if (prepared.title.startsWith(term)) score += 80;
    else if (prepared.title.includes(term)) score += 60;
    else if (prepared.subtitle.includes(term)) score += 35;
    else if (prepared.meta.includes(term) || prepared.keywordText.includes(term)) score += 30;
    else if (prepared.group.includes(term)) score += 25;
    else if (prepared.body.includes(term)) score += 15;
  }
  return score;
}

function compactWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function prepareSearchItems<TItem extends DocumentSearchItem>(
  items: readonly TItem[],
): readonly PreparedDocumentSearchItem<TItem>[] {
  return items.map(prepareSearchItem);
}

function snippetForBody(body: string, lowerBody: string, firstTerm: string | undefined): string {
  if (body.length <= SNIPPET_RADIUS * 2) return body;
  if (firstTerm === undefined) return `${body.slice(0, SNIPPET_RADIUS * 2)}...`;
  const index = lowerBody.indexOf(firstTerm);
  if (index < 0) return `${body.slice(0, SNIPPET_RADIUS * 2)}...`;
  const start = Math.max(0, index - SNIPPET_RADIUS);
  const end = Math.min(body.length, index + firstTerm.length + SNIPPET_RADIUS);
  return `${start > 0 ? "..." : ""}${body.slice(start, end)}${end < body.length ? "..." : ""}`;
}

function matchPreparedSearchItems<TItem extends DocumentSearchItem>(
  preparedItems: readonly PreparedDocumentSearchItem<TItem>[],
  query: string,
  maxResults: number,
): readonly DocumentSearchResult<TItem>[] {
  const terms = words(query);
  const firstTerm = terms[0];
  const scored: { prepared: PreparedDocumentSearchItem<TItem>; score: number }[] = [];
  for (const prepared of preparedItems) {
    const score = scorePreparedItem(prepared, terms);
    if (score > 0) scored.push({ prepared, score });
  }
  scored.sort(
    (a, b) => b.score - a.score || a.prepared.item.title.localeCompare(b.prepared.item.title),
  );
  return scored.slice(0, maxResults).map(({ prepared, score }) => ({
    item: prepared.item,
    score,
    snippet: snippetForBody(prepared.snippetBody, prepared.snippetBodyLower, firstTerm),
  }));
}

export function matchDocumentSearchItems<TItem extends DocumentSearchItem>(
  items: readonly TItem[],
  query: string,
  maxResults = DEFAULT_MAX_RESULTS,
): readonly DocumentSearchResult<TItem>[] {
  return matchPreparedSearchItems(prepareSearchItems(items), query, maxResults);
}

export function DocumentSearch<TItem extends DocumentSearchItem>({
  items,
  onSelect,
  title = "Search",
  placeholder = "Search documents...",
  emptyLabel = "No matches.",
  maxResults = DEFAULT_MAX_RESULTS,
  initialQuery = "",
  "aria-label": ariaLabel = "Search documents",
}: DocumentSearchProps<TItem>): React.JSX.Element {
  const [query, setQuery] = useState(initialQuery);
  const preparedItems = useMemo(() => prepareSearchItems(items), [items]);
  const results = useMemo(
    () => matchPreparedSearchItems(preparedItems, query, maxResults),
    [preparedItems, query, maxResults],
  );

  return (
    <section style={SHELL_STYLE} aria-label={ariaLabel}>
      <style>{`
        .csk-document-search [cmdk-item][data-selected="true"] {
          background: ${PRIMARY[100]};
          border-color: ${PRIMARY[300]};
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Search aria-hidden="true" size={15} color={PRIMARY[700]} />
        <div style={{ color: GRAY[900], fontSize: 12, fontWeight: 800 }}>{title}</div>
        <div style={{ color: GRAY[500], fontSize: 11, marginLeft: "auto" }}>
          {items.length.toLocaleString()} docs
        </div>
      </div>
      <Command className="csk-document-search" shouldFilter={false}>
        <div style={INPUT_WRAP_STYLE}>
          <Command.Input
            value={query}
            onValueChange={setQuery}
            aria-label={ariaLabel}
            placeholder={placeholder}
            style={INPUT_STYLE}
          />
        </div>
        <Command.List style={LIST_STYLE}>
          <Command.Empty style={{ color: GRAY[600], fontSize: 12, padding: 8 }}>
            {emptyLabel}
          </Command.Empty>
          {results.map(({ item, snippet }) => (
            <Command.Item
              key={item.id}
              value={`${item.title} ${item.subtitle ?? ""} ${(item.keywords ?? []).join(" ")}`}
              onSelect={() => onSelect?.(item)}
              style={ITEM_STYLE}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <FileText aria-hidden="true" size={14} color={PRIMARY[700]} />
                <span style={{ color: GRAY[900], fontSize: 12, fontWeight: 800 }}>
                  {item.title}
                </span>
              </div>
              {item.meta !== undefined ? (
                <div style={{ color: PRIMARY[700], fontSize: 10, fontWeight: 700 }}>
                  {item.meta}
                </div>
              ) : null}
              <div style={{ color: GRAY[700], fontSize: 11, lineHeight: 1.45 }}>{snippet}</div>
            </Command.Item>
          ))}
        </Command.List>
      </Command>
    </section>
  );
}
